import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import * as readApi from '../api/readApi';
import { formatDate } from '../utils/dateFormat';
import { sendRegistrationPendingConfirmation, notifyAdminsNewRegistration } from '../services/emailService';
import { useAlert } from '../contexts/AlertContext';
import './TournamentRegistration.css';

export default function TournamentRegistration() {
  const { currentUser, refreshUserProfile } = useAuth();
  const { gameId } = useGame();
  const { showToast } = useAlert();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tournamentIdFromUrl = searchParams.get('tournamentId');
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [registrationsClosed, setRegistrationsClosed] = useState(false);
  const [registrationType, setRegistrationType] = useState({
    singles: false,
    doubles: false
  });
  const [existingRegistrations, setExistingRegistrations] = useState([]);

  useEffect(() => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchData() {
      try {
        if (tournamentIdFromUrl) {
          const data = await readApi.getTournamentRegistrationData(tournamentIdFromUrl, gameId);
          if (cancelled) return;
          setTournament(data.tournament || null);
          setExistingRegistrations(data.existingRegistrations || []);
          setRegistrationsClosed(data.tournament?.registrationsClosed ?? false);
        } else {
          const listData = await readApi.getTournamentList(gameId);
          if (cancelled) return;
          const active = (listData.tournaments || []).filter(t => t.status === 'active');
          const first = active[0] || null;
          if (first) {
            const data = await readApi.getTournamentRegistrationData(first.id, gameId);
            if (cancelled) return;
            setTournament(data.tournament || null);
            setExistingRegistrations(data.existingRegistrations || []);
            setRegistrationsClosed(data.tournament?.registrationsClosed ?? false);
          } else {
            setTournament(null);
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [currentUser?.uid, gameId, tournamentIdFromUrl]);

  async function handleRegister() {
    if (!tournament || registrationsClosed) return;

    if (!registrationType.singles && !registrationType.doubles) {
      setError('Please select at least one registration type (Singles or Doubles)');
      return;
    }

    setRegistering(true);
    setError('');

    try {
      const prevCount = existingRegistrations.length;
      if (registrationType.singles && !existingRegistrations.some(r => r.type === 'singles')) {
        await readApi.registerForTournament(tournament.id, 'singles', currentUser.email);
      }
      if (registrationType.doubles && !existingRegistrations.some(r => r.type === 'doubles')) {
        await readApi.registerForTournament(tournament.id, 'doubles', currentUser.email);
      }

      const data = await readApi.getTournamentRegistrationData(tournament.id, gameId);
      setExistingRegistrations(data.existingRegistrations || []);
      setRegistrationType({ singles: false, doubles: false });

      if ((data.existingRegistrations?.length ?? 0) > prevCount) {
        const types = (data.existingRegistrations || [])
          .filter(r => !existingRegistrations.some(e => e.id === r.id))
          .map(r => r.type);
        const userName = (currentUser.displayName || currentUser.email?.split('@')[0] || 'Player').trim();
        if (currentUser.email) {
          sendRegistrationPendingConfirmation(currentUser.email, tournament.name, types).catch(() => {});
        }
        notifyAdminsNewRegistration({
          tournamentId: tournament.id,
          tournamentName: tournament.name,
          userEmail: currentUser.email,
          userName,
          types
        }).catch(() => {});
        if (refreshUserProfile) await refreshUserProfile();
        showToast('Registration submitted. An admin will approve it soon. You can register for additional types below if needed.', { variant: 'success' });
      }
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  }

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!tournament) {
    return (
      <div className="tournament-registration-container">
        <div className="card">
          <h2>No Active Tournament</h2>
          <p>There is currently no active tournament available for registration.</p>
          <button onClick={() => navigate('/dashboard')} className="btn-secondary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tournament-registration-container">
      <div className="card">
        <h2>{tournament.name}</h2>
        <p className="tournament-description">{tournament.description || 'Join the tournament and compete!'}</p>
        {(tournament.visibility === 'groups' && (tournament.groupIds || []).length > 0) && (
          <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
            This tournament is for members of selected groups. Registrations are limited to approved members of those groups.
          </p>
        )}
        {tournament.startDate && (
          <div className="tournament-info">
            <p><strong>Start Date:</strong> {formatDate(tournament.startDate)}</p>
          </div>
        )}

        {error && <div className="error-message">{error}</div>}

        {registrationsClosed ? (
          <div className="info-message">
            <p>Registrations are now closed for this tournament.</p>
            <button onClick={() => navigate('/dashboard')} className="btn-secondary">
              Back to Dashboard
            </button>
          </div>
        ) : (
          <div>
            {existingRegistrations.length > 0 && (
              <div className="existing-registrations" style={{ marginBottom: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '8px' }}>
                <h4>Your Current Registrations:</h4>
                <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                  {existingRegistrations.map(reg => (
                    <li key={reg.id} style={{ margin: '5px 0' }}>
                      ✓ {reg.type === 'singles' ? '🏓 Singles' : '👥 Doubles'}
                      {reg.teamId && ` - Team ${reg.teamId}`}
                    </li>
                  ))}
                </ul>
                <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                  You can register for additional types below if you haven't already.
                </p>
              </div>
            )}

            <div className="registration-types">
              <h3>Select Registration Type</h3>
              <div className="type-options">
                <label className={`type-option ${registrationType.singles ? 'active' : ''} ${existingRegistrations.some(r => r.type === 'singles') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={registrationType.singles}
                    disabled={existingRegistrations.some(r => r.type === 'singles')}
                    onChange={(e) => setRegistrationType({ ...registrationType, singles: e.target.checked })}
                  />
                  <div className="type-content">
                    <strong>Singles</strong>
                    <span>Individual competition</span>
                    {existingRegistrations.some(r => r.type === 'singles') && (
                      <span style={{ color: '#4caf50', fontSize: '12px', marginTop: '4px' }}>✓ Already registered</span>
                    )}
                  </div>
                </label>
                <label className={`type-option ${registrationType.doubles ? 'active' : ''} ${existingRegistrations.some(r => r.type === 'doubles') ? 'disabled' : ''}`}>
                  <input
                    type="checkbox"
                    checked={registrationType.doubles}
                    disabled={existingRegistrations.some(r => r.type === 'doubles')}
                    onChange={(e) => setRegistrationType({ ...registrationType, doubles: e.target.checked })}
                  />
                  <div className="type-content">
                    <strong>Doubles</strong>
                    <span>Team competition (teams will be created based on skill level)</span>
                    {existingRegistrations.some(r => r.type === 'doubles') && (
                      <span style={{ color: '#4caf50', fontSize: '12px', marginTop: '4px' }}>✓ Already registered</span>
                    )}
                  </div>
                </label>
              </div>
            </div>

            <button 
              onClick={handleRegister} 
              disabled={registering || (!registrationType.singles && !registrationType.doubles)}
              className="btn-primary"
            >
              {registering ? 'Registering...' : existingRegistrations.length > 0 ? 'Register for Additional Types' : 'Register for Tournament'}
            </button>
            <button 
              onClick={() => navigate('/dashboard')} 
              className="btn-secondary"
              style={{ marginTop: '12px' }}
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

